import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
  styled,
  Menu,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import logo from 'src/assets/images/solfin-logo.svg';
import chevronDown from 'src/assets/images/solfin-chevron-down.svg';
import hamburger from 'src/assets/images/solfin-hamburger-menu.svg';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials, setLoading } from 'src/store/slices/authSlice';
import AuthService from 'src/store/services/authService';
import { headers } from 'src/utils/constants';
import { redirectToEpcPartner } from 'src/utils/util';
import { UI_ROUTES } from 'src/utils/routeConstants';

const NavButton = styled(Button)(({ theme, active }) => ({
  color: theme.palette.text.primary,
  display: 'flex',
  fontWeight: '400',
  padding: theme.breakpoints.up('lg') ? '0 24px' : '0 16px',
  borderRadius: 0,
  fontFamily: 'Montserrat',
  height: '64px',
  cursor: 'pointer !important',
  backgroundColor: active ? '#88F231' : 'inherit',
  '&:hover': {
    backgroundColor: '#88F231 !important',
    cursor: 'pointer !important'
  }
}));

const StyledListItemButton = styled(ListItemButton)(() => ({
  cursor: 'pointer !important',
  fontFamily: 'Montserrat',
  margin: '0px 13px',
  borderRadius: '5px',
  '&:hover': {
    width: 'auto',
    backgroundColor: '#88F231 !important'
  }
}));

const StyledListItemText = styled(ListItemText)(() => ({
  '& .MuiListItemText-primary': {
    fontFamily: 'Montserrat !important',
    fontSize: '.8rem',
    fontWeight: '500'
  }
}));

const CustomDropdown = styled(Box)(() => ({
  position: 'absolute',
  top: '64px',
  left: 0,
  backgroundColor: '#FAFAFA',
  minWidth: '240px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  borderRadius: '0 5px 5px 0',
  zIndex: 1000,
  padding: '0',
  transition: 'opacity 150ms ease-in-out, transform 150ms ease-in-out',
  opacity: 0,
  transform: 'translateY(-10px)',
  pointerEvents: 'none',
  '&.visible': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto'
  }
}));

const NestedDropdown = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: '100%',
  backgroundColor: '#FAFAFA',
  minWidth: '240px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  borderRadius: '0 5px 5px 0',
  zIndex: 1000,
  padding: '0',
  transition: 'opacity 150ms ease-in-out, transform 150ms ease-in-out',
  opacity: 0,
  transform: 'translateX(-10px)',
  pointerEvents: 'none',
  color: '#000',
  '&.visible': {
    opacity: 1,
    transform: 'translateX(0)',
    pointerEvents: 'auto'
  }
}));

const DropdownItem = styled(Box)(() => ({
  padding: '10px 24px',
  cursor: 'pointer',
  position: 'relative',
  borderRadius: '0 5px 5px 0',
  fontSize: '.8rem',
  fontFamily: 'Montserrat',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#000',
  '&:hover': {
    backgroundColor: '#D1FFC5'
  },
  '&:hover > div': {
    display: 'block'
  }
}));

const Header = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const iWantSolarRef = useRef(null);
  const financeRef = useRef(null);
  const partnersRef = useRef(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeFinanceSubmenu, setActiveFinanceSubmenu] = useState(null);
  const [activeFinanceMobileSubmenu, setActiveFinanceMobileSubmenu] =
    useState(null);

  const [iWantSolarOpen, setIWantSolarOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState(false);

  const [applyLoanUrl, setApplyLoanUrl] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [loginMenuAnchor, setLoginMenuAnchor] = useState(null);
  const [mobileLoginOpen, setMobileLoginOpen] = useState(false);

  const { isAuthenticated } = useSelector((state) => state.auth);

  const initializeAuth = () => {
    try {
      dispatch(setLoading(true));
      const storedAuth = AuthService.getAuth();

      if (!storedAuth?.token) {
        return;
      }
      dispatch(setCredentials(storedAuth));
    } catch (error) {
      console.error('Error during auth initialization:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    const currentDomain = window.location.hostname;
    setApplyLoanUrl(
      currentDomain.includes('stg') || currentDomain.includes('localhost')
        ? 'https://stg.finance.solfin.co.in'
        : 'https://finance.solfin.co.in'
    );
  }, []);

  useEffect(() => {
    setActiveDropdown(null);
    setActiveFinanceSubmenu(null);
    setIsDrawerOpen(false);
    setIWantSolarOpen(false);
    setFinanceOpen(false);
    setPartnersOpen(false);
    setActiveFinanceMobileSubmenu(null);
    setMobileLoginOpen(false);
    setLoginMenuAnchor(null);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 1);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!isMobile) {
        setIsDrawerOpen(false);
        setActiveDropdown(null);
        setActiveFinanceSubmenu(null);
        setIWantSolarOpen(false);
        setFinanceOpen(false);
        setPartnersOpen(false);
        setActiveFinanceMobileSubmenu(null);
        setMobileLoginOpen(false);
      } else {
        setLoginMenuAnchor(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  useEffect(() => {
    initializeAuth();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isIWantSolarClick =
        iWantSolarRef.current && iWantSolarRef.current.contains(event.target);
      const isFinanceClick =
        financeRef.current && financeRef.current.contains(event.target);
      const isPartnersClick =
        partnersRef.current && partnersRef.current.contains(event.target);

      if (!isIWantSolarClick && !isFinanceClick && !isPartnersClick) {
        setActiveDropdown(null);
        setActiveFinanceSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
    setActiveFinanceSubmenu(null);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openDrawer = () => {
    setIsDrawerOpen(true);
  };

  const toggleMobileSubmenu = (menu) => {
    if (menu === 'iWantSolar') {
      setIWantSolarOpen(!iWantSolarOpen);
    } else if (menu === 'finance') {
      setFinanceOpen(!financeOpen);
      if (financeOpen) {
        setActiveFinanceMobileSubmenu(null);
      }
    } else if (menu === 'partners') {
      setPartnersOpen(!partnersOpen);
    } else if (menu === 'login') {
      setMobileLoginOpen(!mobileLoginOpen);
    } else if (financeOpen) {
      setActiveFinanceMobileSubmenu(
        activeFinanceMobileSubmenu === menu ? null : menu
      );
    }
  };

  const handleLoginMenuClick = (event) => {
    setLoginMenuAnchor(event.currentTarget);
  };

  const handleLoginMenuClose = () => {
    setLoginMenuAnchor(null);
  };

  const handleLoginOptionClick = (loginType) => {
    handleLoginMenuClose();
    if (loginType === 'partner') {
      redirectToEpcPartner('epcPartnerLogin');
    } else {
      setMobileLoginOpen(false);
      setTimeout(() => {
        navigate('/login');
      }, 100);
    }
  };

  const handleMobileLoginOptionClick = (loginType) => {
    setMobileLoginOpen(false);
    setIsDrawerOpen(false);
    if (loginType === 'partner') {
      redirectToEpcPartner('epcPartnerLogin');
    } else {
      navigate('/login');
    }
  };

  const financeMenuStructure = {
    // 'Apply for Loan': [
    //   { label: 'Residential', link: '/register' },
    //   {
    //     label: 'Commercial and Industrial',
    //     link: '/register?locationTypeCode=CMR&customerTypeId=4'
    //   },
    // ],
    'Loan Products': [
      {
        label: 'Residential Rooftop',
        link: '/loan-products/residential-rooftop'
      },
      {
        label: 'Housing Society Rooftop',
        link: '/loan-products/housing-society'
      },
      {
        label: 'Commercial and Industrial',
        link: '/loan-products/commercial-and-industrial'
      },
      {
        label: 'Supply Chain Finance',
        link: `${process.env.REACT_APP_DOMAIN}/v2/supply-chain-finance`
      },
      { label: 'EV Loan', link: '/coming-soon' }
    ]
  };

  const leaseMenuItem = { label: 'Lease', link: UI_ROUTES.leaseContact };

  const renderDesktopFinanceDropdown = () => {
    return (
      <CustomDropdown className={activeDropdown === 'finance' ? 'visible' : ''}>
        {Object.entries(financeMenuStructure).map(([header, items]) => (
          <React.Fragment key={header}>
            <DropdownItem
              onMouseEnter={() => setActiveFinanceSubmenu(header)}
              onMouseLeave={() => setActiveFinanceSubmenu(null)}
              sx={{
                position: 'relative',
                borderRadius: '0',
                '&:hover > div': {
                  display: 'block'
                }
              }}
            >
              {header}
              <ChevronRightIcon fontSize="small" />
              <NestedDropdown
                className={activeFinanceSubmenu === header ? 'visible' : ''}
                sx={{
                  display: activeFinanceSubmenu === header ? 'block' : 'none'
                }}
              >
                {items.map((item) => (
                  <DropdownItem
                    key={item.label}
                    component={item.link.startsWith('http') ? 'a' : Link}
                    href={item.link.startsWith('http') ? item.link : undefined}
                    to={!item.link.startsWith('http') ? item.link : undefined}
                    onClick={() => {
                      sessionStorage.removeItem('cni_applicationReference');
                      sessionStorage.removeItem('scf_preApprovedData');
                      sessionStorage.removeItem('scf_applicationReference');
                      setActiveDropdown(null);
                      setActiveFinanceSubmenu(null);
                    }}
                  >
                    {item.label}
                  </DropdownItem>
                ))}
              </NestedDropdown>
            </DropdownItem>
          </React.Fragment>
        ))}
        <DropdownItem
          component={Link}
          to={leaseMenuItem.link}
          onClick={() => setActiveDropdown(null)}
        >
          {leaseMenuItem.label}
        </DropdownItem>
      </CustomDropdown>
    );
  };

  const renderMobileFinanceDropdown = () => {
    return (
      <Collapse in={financeOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {Object.entries(financeMenuStructure).map(([header, items]) => (
            <React.Fragment key={header}>
              <StyledListItemButton
                sx={{
                  pl: 4,
                  fontWeight: 'medium',
                  justifyContent: 'space-between'
                }}
                onClick={() => toggleMobileSubmenu(header)}
              >
                <StyledListItemText primary={header} />
                {activeFinanceMobileSubmenu === header ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </StyledListItemButton>
              <Collapse
                in={activeFinanceMobileSubmenu === header}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {items.map((item) => (
                    <StyledListItemButton
                      key={item.label}
                      component={item.link.startsWith('http') ? 'a' : Link}
                      href={
                        item.link.startsWith('http') ? item.link : undefined
                      }
                      to={!item.link.startsWith('http') ? item.link : undefined}
                      onClick={closeDrawer}
                      sx={{
                        pl: 6,
                        bgcolor: isActiveRoute(item.link)
                          ? '#D1FFC5'
                          : 'inherit'
                      }}
                    >
                      <StyledListItemText primary={item.label} />
                    </StyledListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
          <StyledListItemButton
            component={Link}
            to={leaseMenuItem.link}
            onClick={closeDrawer}
            sx={{
              pl: 4,
              bgcolor: isActiveRoute(leaseMenuItem.link) ? '#D1FFC5' : 'inherit'
            }}
          >
            <StyledListItemText primary={leaseMenuItem.label} />
          </StyledListItemButton>
        </List>
      </Collapse>
    );
  };

  const isActiveRoute = (path) => {
    const currentPath = location.pathname;

    return currentPath === path;
  };

  return (
    <>
      <Helmet>
        <title>
          Solfin - Solar Financing Solutions for Residential, C & I and Others
        </title>
        <meta
          name="description"
          content="Solfin offers comprehensive solar financing solutions for residential, commercial, and industrial sectors. Explore project financing, equipment loans, and more. Start your solar journey today!"
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Solfin - Solar Financing Solutions for Residential, C & I and Others"
        />
        <meta
          property="og:description"
          content="Discover solar financing options at Solfin. Get residential, commercial, and industrial solar loans, equipment financing, and savings insights."
        />
        <meta property="og:image" content={logo} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <AppBar
        position="fixed"
        color="default"
        elevation={isScrolled ? 3 : 0}
        sx={{
          bgcolor: '#FAFAFA',
          padding: 0,
          transition: 'box-shadow 0.3s ease-in-out',
          zIndex: (theme) => theme.zIndex.drawer - 1
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {isMobile && (
              <Box
                sx={{
                  width: '80px'
                }}
              >
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={openDrawer}
                  sx={{ cursor: 'pointer !important' }}
                >
                  <img src={hamburger} alt="menu" />
                </IconButton>
              </Box>
            )}
            <Box
              component={Link}
              to="/"
              sx={{
                flexGrow: { xs: 1, md: 0 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', lg: 'start' },
                textDecoration: 'none',
                mr: { xs: 0, md: 2 },
                cursor: 'pointer !important'
              }}
            >
              <img src={logo} alt="Solfin Logo" style={{ height: '40px' }} />
            </Box>
            {!isMobile && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <NavButton onClick={() => navigate('/')}>HOME</NavButton>
                <Box ref={iWantSolarRef} sx={{ position: 'relative' }}>
                  <NavButton
                    onClick={() => handleToggleDropdown('iWantSolar')}
                    endIcon={<img src={chevronDown} alt="chevron-down" />}
                    active={activeDropdown === 'iWantSolar'}
                  >
                    I WANT SOLAR
                  </NavButton>
                  <CustomDropdown
                    className={activeDropdown === 'iWantSolar' ? 'visible' : ''}
                  >
                    <DropdownItem
                      component={Link}
                      to="/solar-calculator"
                      onClick={() => setActiveDropdown(null)}
                    >
                      Calculate my savings
                    </DropdownItem>
                    {isAuthenticated && (
                      <DropdownItem
                        component={Link}
                        to="/register"
                        onClick={() => setActiveDropdown(null)}
                      >
                        Register
                      </DropdownItem>
                    )}
                  </CustomDropdown>
                </Box>

                <Box ref={financeRef} sx={{ position: 'relative' }}>
                  <NavButton
                    onClick={() => handleToggleDropdown('finance')}
                    endIcon={<img src={chevronDown} alt="chevron-down" />}
                    active={activeDropdown === 'finance'}
                  >
                    FINANCE
                  </NavButton>
                  {renderDesktopFinanceDropdown()}
                </Box>

                {!isAuthenticated ? (
                  <Box ref={partnersRef} sx={{ position: 'relative' }}>
                    <NavButton
                      onClick={() => handleToggleDropdown('partners')}
                      endIcon={<img src={chevronDown} alt="chevron-down" />}
                      active={activeDropdown === 'partners'}
                    >
                      {headers?.partners?.title.toUpperCase()}
                    </NavButton>
                    <CustomDropdown
                      className={activeDropdown === 'partners' ? 'visible' : ''}
                    >
                      {headers?.partners?.subroutes.map((subroute) => (
                        <DropdownItem
                          key={subroute.routePath}
                          component={Link}
                          to={subroute.routePath}
                          onClick={() => setActiveDropdown(null)}
                        >
                          {subroute?.title}
                        </DropdownItem>
                      ))}
                    </CustomDropdown>
                  </Box>
                ) : null}

                <NavButton component="a" href="/v2/mission">
                  MISSION
                </NavButton>
                <NavButton onClick={() => navigate('/ad-campaigns')}>
                  MEDIA
                </NavButton>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {!isMobile && (
                <>
                  {isAuthenticated ? (
                    <Button
                      component={Link}
                      to="/dashboard"
                      variant="contained"
                      color="primary"
                      sx={{
                        ml: 2,
                        borderRadius: 10,
                        bgcolor: '#000',
                        color: '#88F231',
                        px: 3,
                        fontFamily: 'Montserrat',
                        cursor: 'pointer !important',
                        '&:hover': {
                          color: '#88F231',
                          bgcolor: '#000',
                          fontFamily: 'Montserrat'
                        }
                      }}
                    >
                      Dashboard
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleLoginMenuClick}
                        endIcon={
                          <Box
                            sx={{
                              transform: loginMenuAnchor
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                              transition: 'transform 0.3s ease-in-out',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <img
                              src={chevronDown}
                              width={20}
                              alt="chevron-down"
                            />
                          </Box>
                        }
                        sx={{
                          ml: 2,
                          borderRadius: 10,
                          bgcolor: '#000',
                          color: '#88F231',
                          px: 3,
                          fontFamily: 'Montserrat',
                          cursor: 'pointer !important',
                          '&:hover': {
                            color: '#88F231',
                            bgcolor: '#000',
                            fontFamily: 'Montserrat'
                          }
                        }}
                      >
                        Login
                      </Button>
                      <Menu
                        anchorEl={loginMenuAnchor}
                        open={Boolean(loginMenuAnchor)}
                        onClose={handleLoginMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center'
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'center'
                        }}
                        TransitionProps={{
                          timeout: 300
                        }}
                        PaperProps={{
                          sx: {
                            bgcolor: '#FAFAFA',
                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                            borderRadius: '5px',
                            mt: 1,
                            minWidth: '160px',
                            overflow: 'hidden'
                          }
                        }}
                      >
                        <MenuItem
                          onClick={() => handleLoginOptionClick('customer')}
                          sx={{
                            fontFamily: 'Montserrat',
                            fontSize: '0.9rem',
                            px: 3,
                            py: 1.5,
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            '&:hover': {
                              backgroundColor: '#D1FFC5',
                              transform: 'translateX(4px)'
                            }
                          }}
                        >
                          <PersonIcon
                            sx={{
                              fontSize: '1.1rem',
                              color: '#666',
                              transition: 'color 0.2s ease-in-out'
                            }}
                          />
                          For Customer
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleLoginOptionClick('partner')}
                          sx={{
                            fontFamily: 'Montserrat',
                            fontSize: '0.9rem',
                            px: 3,
                            py: 1,
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            '&:hover': {
                              backgroundColor: '#D1FFC5',
                              transform: 'translateX(4px)',
                              '& .MuiSvgIcon-root': {
                                color: '#333'
                              }
                            }
                          }}
                        >
                          <BusinessIcon
                            sx={{
                              fontSize: '1.1rem',
                              color: '#666',
                              transition: 'color 0.2s ease-in-out'
                            }}
                          />
                          For Partner
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Toolbar />

      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: { width: '310px' }
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box
            component={Link}
            to="/"
            onClick={closeDrawer}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer !important'
            }}
          >
            <img src={logo} alt="Solfin Logo" style={{ height: '32px' }} />
          </Box>
          <IconButton
            onClick={closeDrawer}
            sx={{ cursor: 'pointer !important' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <List
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <ListItem disablePadding>
            <StyledListItemButton
              component={Link}
              to="/"
              onClick={closeDrawer}
              sx={{
                bgcolor: isActiveRoute('/') ? '#D1FFC5' : 'inherit'
              }}
            >
              <StyledListItemText primary="HOME" />
            </StyledListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <StyledListItemButton
              onClick={() => toggleMobileSubmenu('iWantSolar')}
              sx={{
                bgcolor:
                  isActiveRoute('/solar-calculator') ||
                  isActiveRoute('/register')
                    ? '#D1FFC5'
                    : 'inherit'
              }}
            >
              <StyledListItemText primary="I WANT SOLAR" />
              {iWantSolarOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </StyledListItemButton>
          </ListItem>
          <Collapse in={iWantSolarOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <StyledListItemButton
                component={Link}
                to="/solar-calculator"
                onClick={closeDrawer}
                sx={{
                  pl: 4,
                  bgcolor: isActiveRoute('/solar-calculator')
                    ? '#D1FFC5'
                    : 'inherit'
                }}
              >
                <StyledListItemText primary="Calculate my savings" />
              </StyledListItemButton>
              {isAuthenticated && (
                <StyledListItemButton
                  component={Link}
                  to="/register"
                  onClick={closeDrawer}
                  sx={{
                    pl: 4,
                    bgcolor: isActiveRoute('/register') ? '#D1FFC5' : 'inherit'
                  }}
                >
                  <StyledListItemText primary="Register" />
                </StyledListItemButton>
              )}
            </List>
          </Collapse>

          <ListItem disablePadding>
            <StyledListItemButton
              onClick={() => toggleMobileSubmenu('finance')}
              sx={{
                bgcolor: Object.values(financeMenuStructure).some((subItems) =>
                  subItems.some((item) =>
                    isActiveRoute(item.link.replace(/^https?:\/\/[^/]+/, ''))
                  )
                )
                  ? '#D1FFC5'
                  : 'inherit'
              }}
            >
              <StyledListItemText primary="FINANCE" />
              {financeOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </StyledListItemButton>
          </ListItem>
          {renderMobileFinanceDropdown()}

          {!isAuthenticated && (
            <ListItem disablePadding>
              <StyledListItemButton
                onClick={() => toggleMobileSubmenu('partners')}
                sx={{
                  bgcolor: headers.partners.subroutes.some((route) =>
                    isActiveRoute(route.routePath)
                  )
                    ? '#D1FFC5'
                    : 'inherit'
                }}
              >
                <StyledListItemText
                  primary={headers.partners.title.toUpperCase()}
                />
                {partnersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </StyledListItemButton>
            </ListItem>
          )}

          <Collapse in={partnersOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {headers.partners.subroutes.map((route) => (
                <StyledListItemButton
                  key={route.routePath}
                  component={Link}
                  to={route.routePath}
                  onClick={closeDrawer}
                  sx={{
                    pl: 4,
                    bgcolor: isActiveRoute(route.routePath)
                      ? '#D1FFC5'
                      : 'inherit'
                  }}
                >
                  <StyledListItemText primary={route.title} />
                </StyledListItemButton>
              ))}
            </List>
          </Collapse>
          <ListItem disablePadding>
            <StyledListItemButton
              component="a"
              href="/v2/mission"
              onClick={closeDrawer}
            >
              <StyledListItemText primary="MISSION" />
            </StyledListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <StyledListItemButton
              component={Link}
              to="/ad-campaigns"
              onClick={closeDrawer}
              sx={{
                bgcolor: isActiveRoute('/ad-campaigns') ? '#D1FFC5' : 'inherit'
              }}
            >
              <StyledListItemText primary="MEDIA" />
            </StyledListItemButton>
          </ListItem>

          {!isAuthenticated && (
            <Box
              sx={{
                marginTop: 'auto',
                display: 'flex',
                flexDirection: 'column-reverse'
              }}
            >
              <ListItem disablePadding>
                <StyledListItemButton
                  onClick={() => toggleMobileSubmenu('login')}
                  sx={{
                    bgcolor: '#000',
                    color: '#fff',
                    m: 2,
                    borderRadius: 2,
                    gap: 2,
                    justifyContent: 'space-between',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      // bgcolor: '#333',
                      transform: 'translateX(2px)'
                    }
                  }}
                >
                  <StyledListItemText
                    primary="Login"
                    sx={{ textAlign: 'center', flexGrow: 1 }}
                  />
                  <Box
                    sx={{
                      transition: 'transform 0.3s ease-in-out',
                      transform: mobileLoginOpen
                        ? 'rotate(0deg)'
                        : 'rotate(180deg)',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ExpandLessIcon />
                  </Box>
                </StyledListItemButton>
              </ListItem>

              <Collapse
                in={mobileLoginOpen}
                timeout={{
                  enter: 400,
                  exit: 300
                }}
                unmountOnExit
                sx={{
                  '& .MuiCollapse-wrapper': {
                    '& .MuiCollapse-wrapperInner': {
                      transform: mobileLoginOpen
                        ? 'translateY(0)'
                        : 'translateY(10px)',
                      transition: 'transform 0.3s ease-out'
                    }
                  }
                }}
              >
                <List component="div" disablePadding>
                  <StyledListItemButton
                    onClick={() => handleMobileLoginOptionClick('customer')}
                    sx={{
                      pl: 3,
                      pr: 3,
                      width: '24px',
                      m: 1,
                      mx: 2,
                      borderRadius: 1,
                      bgcolor: '#f5f5f5',
                      transition: 'all 0.2s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      '&:hover': {
                        bgcolor: '#D1FFC5',
                        transform: 'translateX(4px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '& .MuiSvgIcon-root': {
                          color: '#333'
                        }
                      }
                    }}
                  >
                    <PersonIcon
                      sx={{
                        fontSize: '1.2rem',
                        color: '#666',
                        transition: 'color 0.2s ease-in-out'
                      }}
                    />
                    <StyledListItemText
                      primary="Customer"
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '0.9rem'
                        }
                      }}
                    />
                  </StyledListItemButton>
                  <StyledListItemButton
                    onClick={() => handleMobileLoginOptionClick('partner')}
                    sx={{
                      pl: 3,
                      pr: 3,
                      width: '24px',
                      m: 1,
                      mx: 2,
                      borderRadius: 1,
                      bgcolor: '#f5f5f5',
                      transition: 'all 0.2s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      '&:hover': {
                        bgcolor: '#D1FFC5',
                        transform: 'translateX(4px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '& .MuiSvgIcon-root': {
                          color: '#333'
                        }
                      }
                    }}
                  >
                    <BusinessIcon
                      sx={{
                        fontSize: '1.2rem',
                        color: '#666',
                        transition: 'color 0.2s ease-in-out'
                      }}
                    />
                    <StyledListItemText
                      primary="Partner"
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '0.9rem'
                        }
                      }}
                    />
                  </StyledListItemButton>
                </List>
              </Collapse>
            </Box>
          )}

          {isAuthenticated && (
            <ListItem
              disablePadding
              sx={{
                marginTop: 'auto'
              }}
            >
              <StyledListItemButton
                component={Link}
                to="/dashboard"
                onClick={closeDrawer}
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  m: 2,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: '#333'
                  }
                }}
              >
                <StyledListItemText
                  primary="Dashboard"
                  sx={{ textAlign: 'center' }}
                />
              </StyledListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>
    </>
  );
};

export default Header;
